import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  ArrowBack,
  Flag,
  Warning,
  CheckCircle,
  Block,
  Delete,
  Article,
  Comment,
} from '@mui/icons-material';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ContentReport {
  id: string;
  type: 'post' | 'comment';
  contentId: string;
  reporterUserId: string;
  reportedUserId: string;
  reason: string;
  description: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: Date;
  resolvedAt?: Date;
  content?: any;
  reporter?: any;
  reportedUser?: any;
}

interface ModerationStats {
  totalReports: number;
  pendingReports: number;
  reviewedReports: number;
  resolvedReports: number;
  dismissedReports: number;
  averageResolutionTime: number;
  reportsByType: { type: string; count: number }[];
  reportsByReason: { reason: string; count: number }[];
}

const ContentModerationPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [stats, setStats] = useState<ModerationStats | null>(null);
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resolve' | 'dismiss' | 'delete'>('resolve');
  const [actionNote, setActionNote] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [currentTab] = useState(0); // eslint-disable-line @typescript-eslint/no-unused-vars

  const fetchModerationData = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const [reportsResponse] = await Promise.all([
        axios.get('https://api.sobcof.com/api/content-reports', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/posts', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/comments', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
      ]);

      if (reportsResponse.data.success) {
        // API returns { reports: [...], total, limit, offset }
        const reportsData = reportsResponse.data.data?.reports || [];
        setReports(Array.isArray(reportsData) ? reportsData : []);

        // Calculate stats
        const pending = reportsData.filter((r: ContentReport) => r.status === 'pending').length;
        const reviewed = reportsData.filter((r: ContentReport) => r.status === 'reviewed').length;
        const resolved = reportsData.filter((r: ContentReport) => r.status === 'resolved').length;
        const dismissed = reportsData.filter((r: ContentReport) => r.status === 'dismissed').length;

        // Calculate average resolution time
        const resolvedReports = reportsData.filter((r: ContentReport) => r.resolvedAt);
        const avgTime = resolvedReports.length > 0
          ? resolvedReports.reduce((sum: number, r: ContentReport) => {
            const created = new Date(r.createdAt).getTime();
            const resolved = new Date(r.resolvedAt!).getTime();
            return sum + (resolved - created);
          }, 0) / resolvedReports.length / (1000 * 60 * 60) // Convert to hours
          : 0;

        // Group by type
        const typeGroups = reportsData.reduce((acc: any, r: ContentReport) => {
          acc[r.type] = (acc[r.type] || 0) + 1;
          return acc;
        }, {});

        // Group by reason
        const reasonGroups = reportsData.reduce((acc: any, r: ContentReport) => {
          acc[r.reason] = (acc[r.reason] || 0) + 1;
          return acc;
        }, {});

        setStats({
          totalReports: reportsData.length,
          pendingReports: pending,
          reviewedReports: reviewed,
          resolvedReports: resolved,
          dismissedReports: dismissed,
          averageResolutionTime: avgTime,
          reportsByType: Object.entries(typeGroups).map(([type, count]) => ({ type, count: count as number })),
          reportsByReason: Object.entries(reasonGroups).map(([reason, count]) => ({ reason, count: count as number })),
        });
      }
    } catch (error) {
      console.error('Error fetching moderation data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModerationData();
  }, [fetchModerationData]);

  const handleAction = async () => {
    if (!selectedReport) return;

    try {
      const token = localStorage.getItem('adminToken');

      if (actionType === 'delete') {
        // Delete the content
        const endpoint = selectedReport.type === 'post'
          ? `https://api.sobcof.com/api/posts/${selectedReport.contentId}`
          : `https://api.sobcof.com/api/comments/${selectedReport.contentId}`;

        await axios.delete(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      // Update report status
      const newStatus = actionType === 'resolve' ? 'resolved' : actionType === 'dismiss' ? 'dismissed' : 'resolved';
      await axios.patch(`https://api.sobcof.com/api/content-reports/${selectedReport.id}/status`, {
        status: newStatus,
        note: actionNote
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setActionDialogOpen(false);
      setSelectedReport(null);
      setActionNote('');
      fetchModerationData();
    } catch (error) {
      console.error('Error handling action:', error);
      alert('İşlem başarısız oldu');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'resolved': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'reviewed': return 'İnceleniyor';
      case 'resolved': return 'Çözüldü';
      case 'dismissed': return 'Reddedildi';
      default: return status;
    }
  };

  const COLORS = ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F4A460', '#FF6B6B'];

  const filteredReports = reports.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterType !== 'all' && r.type !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#8B4513' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
          boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <Flag sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            İçerik Moderasyonu
          </Typography>
          <Badge badgeContent={stats?.pendingReports || 0} color="error">
            <Warning sx={{ color: 'white' }} />
          </Badge>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Key Metrics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#FF6B6B', mr: 2 }}>
                    <Flag />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF6B6B' }}>
                      {stats?.totalReports || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Rapor
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#FFA726', mr: 2 }}>
                    <Warning />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFA726' }}>
                      {stats?.pendingReports || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Bekleyen
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#4CAF50', mr: 2 }}>
                    <CheckCircle />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                      {stats?.resolvedReports || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Çözüldü
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#2196F3', mr: 2 }}>
                    <Article />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196F3' }}>
                      {stats?.averageResolutionTime.toFixed(1) || 0}h
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ortalama Çözüm Süresi
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Charts */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Rapor Türü Dağılımı
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats?.reportsByType || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, count }) => `${type === 'post' ? 'Post' : 'Yorum'}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {(stats?.reportsByType || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Rapor Sebepleri
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats?.reportsByReason || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="reason" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#FF6B6B" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Box>
        </Box>

        {/* Filters */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Durum Filtrele</InputLabel>
                <Select
                  value={filterStatus}
                  label="Durum Filtrele"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="pending">Bekleyen</MenuItem>
                  <MenuItem value="reviewed">İnceleniyor</MenuItem>
                  <MenuItem value="resolved">Çözüldü</MenuItem>
                  <MenuItem value="dismissed">Reddedildi</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tür Filtrele</InputLabel>
                <Select
                  value={filterType}
                  label="Tür Filtrele"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="post">Post</MenuItem>
                  <MenuItem value="comment">Yorum</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Card>

        {/* Reports Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tür</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sebep</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Raporlayan</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow
                    key={report.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f8fafc' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Chip
                        icon={report.type === 'post' ? <Article /> : <Comment />}
                        label={report.type === 'post' ? 'Post' : 'Yorum'}
                        size="small"
                        color={report.type === 'post' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.reason}</Typography>
                      {report.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {report.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.reporterUserId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(report.status)}
                        size="small"
                        color={getStatusColor(report.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(report.createdAt).toLocaleDateString('tr-TR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedReport(report);
                            setActionType('resolve');
                            setActionDialogOpen(true);
                          }}
                          disabled={report.status === 'resolved'}
                        >
                          <CheckCircle fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => {
                            setSelectedReport(report);
                            setActionType('dismiss');
                            setActionDialogOpen(true);
                          }}
                          disabled={report.status === 'dismissed'}
                        >
                          <Block fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedReport(report);
                            setActionType('delete');
                            setActionDialogOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'resolve' && 'Raporu Çöz'}
          {actionType === 'dismiss' && 'Raporu Reddet'}
          {actionType === 'delete' && 'İçeriği Sil'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionType === 'resolve' && 'Bu raporu çözüldü olarak işaretlemek istediğinizden emin misiniz?'}
            {actionType === 'dismiss' && 'Bu raporu reddetmek istediğinizden emin misiniz?'}
            {actionType === 'delete' && 'Bu içeriği kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Not (Opsiyonel)"
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color={actionType === 'delete' ? 'error' : 'primary'}
          >
            Onayla
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentModerationPage;
